import { useLogin, useVerifyTwoFactor } from "api/Auth/auth";
import { Spinner } from "components";
import { createAlert } from "components/Alert/Alert";
import AuthShell from "components/Auth/AuthShell";
import TwoFactorChallenge from "components/Auth/TwoFactorChallenge";
import InputField from "components/InputField/InputField";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

export default function Login() {
  const { role } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [twoFactorChallenge, setTwoFactorChallenge] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const { mutate: login, isPending } = useLogin({
    onTwoFactorRequired: (challenge) => {
      setTwoFactorChallenge(challenge);
      setTwoFactorCode("");
    },
    onAuthenticated: () => {
      setTwoFactorChallenge(null);
      setTwoFactorCode("");
    },
  });

  const { mutate: verifyTwoFactor, isPending: isTwoFactorPending } =
    useVerifyTwoFactor({
      onAuthenticated: () => {
        setTwoFactorChallenge(null);
        setTwoFactorCode("");
      },
    });

  useEffect(() => {
    if (role === "admin") navigate("/dashboard");
    if (role === "customer") navigate("/control-panel/categories");
  }, [role, navigate]);

  const handleSubmitLogin = async (e) => {
    e.preventDefault();

    if (twoFactorChallenge) {
      if (twoFactorCode.trim().length < 6) {
        createAlert("Warning", "يرجى إدخال رمز التحقق.");
        return;
      }

      verifyTwoFactor({
        challenge_id: twoFactorChallenge.challenge_id,
        code: twoFactorCode,
      });
      return;
    }

    if (email === "" || password === "") {
      createAlert("Warning", "جميع الحقول مطلوبة.");
      return;
    }

    if (password.length < 8) {
      createAlert("Warning", "كلمة المرور يجب أن تكون 8 أحرف على الأقل.");
      return;
    }

    login({ email, password });
  };

  return (
    <AuthShell
      badge="تسجيل الدخول"
      title={twoFactorChallenge ? "التحقق بخطوتين" : "ادخل إلى حسابك"}
      description={
        twoFactorChallenge
          ? "أكمل التحقق لإتمام الدخول بأمان."
          : "سجّل الدخول للوصول إلى طلباتك ورصيدك وخدمات المتجر."
      }
      asideTitle="خدمات رقمية سريعة"
      asideDescription="يوفر SHAM خدمات شحن الألعاب والتطبيقات والبطاقات الرقمية ضمن واجهة سريعة وواضحة."
      summaryItems={[
        {
          title: "شحن الألعاب",
          description: "PUBG وFree Fire وخدمات الألعاب المشابهة.",
        },
        {
          title: "التطبيقات والدردشة",
          description: "شحن التطبيقات وخدمات الدردشة والرصيد الرقمي.",
        },
        {
          title: "البطاقات الرقمية",
          description: "بطاقات PlayStation وiTunes وAmazon وغيرها.",
        },
      ]}
    >
      <form onSubmit={handleSubmitLogin} className="grid gap-3">
        {!twoFactorChallenge ? (
          <>
            <InputField
              title="البريد الإلكتروني"
              id="emailAddress"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.value)}
              direction="ltr"
              placeholder="name@example.com"
            />

            <InputField
              title="كلمة المرور"
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.value)}
              direction="ltr"
              placeholder="********"
            />

            <div
              className="grid gap-1.5 text-sm"
              style={{ color: "var(--layout-text-muted)" }}
            >
              <span>
                هل نسيت كلمة المرور؟{" "}
                <Link
                  className="font-bold portal-accent hover:underline"
                  to="/forgot-my-password"
                >
                  استعدها من هنا
                </Link>
              </span>
              <span>
                ليس لديك حساب؟{" "}
                <Link
                  className="font-bold portal-accent hover:underline"
                  to="/register"
                >
                  أنشئ حسابًا جديدًا
                </Link>
              </span>
            </div>

            <button
              className="portal-accent-bg mx-auto inline-flex min-w-40 items-center justify-center rounded-xl px-6 py-3 font-bold text-white"
              disabled={isPending}
              type="submit"
            >
              {isPending ? <Spinner sm /> : "تسجيل الدخول"}
            </button>
          </>
        ) : (
          <TwoFactorChallenge
            code={twoFactorCode}
            onChange={setTwoFactorCode}
            onSubmit={handleSubmitLogin}
            onCancel={() => {
              setTwoFactorChallenge(null);
              setTwoFactorCode("");
            }}
            isPending={isTwoFactorPending}
          />
        )}
      </form>
    </AuthShell>
  );
}
